import { useCallback, useEffect, useMemo, useState } from 'react';
import { faFileAlt, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useDropdown } from '../common/index.js';
import { calendarDateDaysAgo, formatCalendarDate, todayCalendarDate } from '../utils/calendarDate.js';
import { CONSULTATION_ACTIONS, DOCUMENT_ACTIONS, TASK_ACTIONS, UserAction, UserActionLabels } from './userActionEnum.js';
import { ALL_INSIGHTS_TYPE_OPTIONS, defaultFiltersForInsightsType } from './insightsTypes.js';

const useInsightsQuery = ({ teams, userIdToName, hasDocsRead, hasTaskRead, hasConsultRead }) => {
  const [insightsType, setInsightsType] = useState('documents');
  const [viewMode, setViewMode] = useState('gp-practice');
  const [selectedUserIds, setSelectedUserIds] = useState(null);
  const [selectedActions, setSelectedActions] = useState([UserAction.DELETE, UserAction.FILE_AWAY, UserAction.CREATE]);
  const [focusedAction, setFocusedAction] = useState(UserAction.FILE_AWAY);
  const [dateRange, setDateRange] = useState({ value: 'last7days', display: 'Last 7 days' });
  const [customDateRange, setCustomDateRange] = useState({
    from: calendarDateDaysAgo(7),
    to: todayCalendarDate()
  });
  const [filterBarActionOpen, setFilterBarActionOpen] = useState(false);
  const [overTimeDropdownOpen, setOverTimeDropdownOpen] = useState(false);
  const [byStaffDropdownOpen, setByStaffDropdownOpen] = useState(false);
  const [userFilterOpen, setUserFilterOpen] = useState(false);
  const [actionFilterOpen, setActionFilterOpen] = useState(false);

  const dateRangeParams = useMemo(() => ({
    rangeValue: dateRange.value,
    customFrom: customDateRange.from,
    customTo: customDateRange.to
  }), [dateRange.value, customDateRange.from, customDateRange.to]);

  const dateRangeDropdown = useDropdown({ value: 'last7days', display: 'Last 7 days' });
  const insightsTypeDropdown = useDropdown({ value: 'documents', display: 'Documents' });
  const documentsReadyDropdown = useDropdown({ value: 'documentsAssigned', display: 'Documents Assigned' });
  const tasksReadyDropdown = useDropdown({ value: 'tasksAssigned', display: 'Tasks Assigned' });
  const processingTimeDropdown = useDropdown({ value: 'timeToReview', display: 'Turnaround Time' });
  const consultationProcessingTimeDropdown = useDropdown({ value: 'recordingDuration', display: 'Recording Duration' });
  const focusedActionDropdown = useDropdown({ value: UserAction.FILE_AWAY, display: UserActionLabels[UserAction.FILE_AWAY] });

  useEffect(() => {
    const stored = sessionStorage.getItem('insightsFilters');
    if (stored) {
      try {
        const { userIds, actions, focused, insightsType: storedInsightsType } = JSON.parse(stored);
        if (storedInsightsType) setInsightsType(storedInsightsType);
        if (userIds) setSelectedUserIds(userIds);
        if (actions && actions.length > 0) setSelectedActions(actions);
        if (focused) setFocusedAction(focused);
      } catch (e) {
        console.error('Failed to parse session storage:', e);
      }
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('insightsFilters', JSON.stringify({
      insightsType: insightsType,
      userIds: selectedUserIds,
      actions: selectedActions,
      focused: focusedAction
    }));
  }, [insightsType, selectedUserIds, selectedActions, focusedAction]);

  const getExpandedUserIds = useCallback(() => {
    if (!selectedUserIds) return null;

    const expanded = new Set();
    selectedUserIds.forEach(id => {
      const team = teams.find(t => t.teamId === id);
      if (team) {
        team.userIds.forEach(uid => expanded.add(uid));
      } else {
        expanded.add(id);
      }
    });
    return Array.from(expanded);
  }, [selectedUserIds, teams]);

  const handleUserSelectionChange = useCallback((selectedIds) => {
    setSelectedUserIds(selectedIds.length === 0 ? null : selectedIds);
  }, []);

  const handleActionSelectionChange = useCallback((actions) => {
    const newActions = actions.length === 0
      ? defaultFiltersForInsightsType(insightsType).selectedActions
      : actions;
    setSelectedActions(newActions);

    if (!newActions.includes(focusedAction)) {
      setFocusedAction(newActions[0]);
    }
  }, [focusedAction, insightsType]);

  const handleFocusedActionChange = useCallback((option) => {
    setFocusedAction(option.value);
    focusedActionDropdown.handleSelect(option);
    setFilterBarActionOpen(false);
    setOverTimeDropdownOpen(false);
    setByStaffDropdownOpen(false);
  }, [focusedActionDropdown]);

  const handleToggleAction = useCallback((action) => {
    if (!selectedActions.includes(action)) {
      setSelectedActions(prev => [...prev, action]);
    } else {
      const newActions = selectedActions.filter(a => a !== action);
      if (newActions.length === 0) {
        setSelectedActions(defaultFiltersForInsightsType(insightsType).selectedActions);
      } else {
        setSelectedActions(newActions);
      }
    }
  }, [selectedActions, insightsType]);

  const getDateRange = useCallback(() => {
    const now = new Date();
    const to = new Date();
    let from;

    switch (dateRangeParams.rangeValue) {
      case 'last24h':
        from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'last7days':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'lastMonth':
        from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        from = new Date(dateRangeParams.customFrom);
        const selectedToDate = new Date(dateRangeParams.customTo);

        const endOfSelectedDay = new Date(selectedToDate);
        endOfSelectedDay.setHours(23, 59, 59, 999);

        to.setTime(Math.min(endOfSelectedDay.getTime(), now.getTime()));
        break;
      default:
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    }

    return { from, to };
  }, [dateRangeParams.rangeValue, dateRangeParams.customFrom, dateRangeParams.customTo]);

  const handleDateRangeSelect = useCallback((option) => {
    if (option.value !== 'custom') {
      const now = new Date();
      let from;

      switch (option.value) {
        case 'last24h':
          from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case 'last7days':
          from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'lastMonth':
          from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      }

      setCustomDateRange({
        from: formatCalendarDate(from),
        to: todayCalendarDate(),
      });
    }

    setDateRange(option);
    dateRangeDropdown.handleSelect(option);
  }, [dateRangeDropdown]);

  const handleCustomDateChange = useCallback((field, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleViewModeChange = useCallback((newMode) => {
    setViewMode(newMode);
  }, []);

  const handleInsightsTypeChange = useCallback((option) => {
    const newType = option.value;
    setInsightsType(newType);
    const defaults = defaultFiltersForInsightsType(newType);
    setSelectedActions(defaults.selectedActions);
    setFocusedAction(defaults.focusedAction);
  }, []);

  const getActionTitle = useCallback((action) => {
    const label = UserActionLabels[action] || action;
    return label;
  }, []);

  const getUserSelectionText = useCallback(() => {
    const totalUsers = Object.keys(userIdToName).length;
    if (!selectedUserIds || selectedUserIds.length === 0) {
      return `All ${totalUsers} users`;
    }
    return `${selectedUserIds.length}/${totalUsers + 1} users selected`;
  }, [selectedUserIds, userIdToName]);

  const getUsersAndTeamsOptions = useCallback(() => {
    const options = [];

    options.push({
      id: 'SYSTEM',
      name: 'MedPrompt',
      display: 'MedPrompt',
      type: 'user',
      icon: faUser
    });

    teams.forEach(team => {
      options.push({
        id: team.teamId,
        name: team.teamName,
        display: team.teamName,
        type: 'team',
        icon: faUsers,
        userIds: team.userIds
      });
    });

    Object.entries(userIdToName).forEach(([userId, name]) => {
      if (userId !== 'SYSTEM') {
        options.push({
          id: userId,
          name: name,
          display: name,
          type: 'user',
          icon: faUser
        });
      }
    });

    return options;
  }, [teams, userIdToName]);

  const getActionOptions = useCallback(() => {
    const actions = insightsType === 'documents'
      ? DOCUMENT_ACTIONS
      : insightsType === 'consultations'
        ? CONSULTATION_ACTIONS
        : TASK_ACTIONS;
    return actions.map(action => ({
      id: action,
      display: UserActionLabels[action],
      value: action
    }));
  }, [insightsType]);

  const documentsReadyOptions = useMemo(() => [
    { value: 'documentsAssigned', display: 'Documents Assigned' },
    { value: 'totalDocumentsOpenedByUser', display: 'Documents Opened' }
  ], []);

  const tasksReadyOptions = useMemo(() => [
    { value: 'tasksAssigned', display: 'Tasks Assigned' },
    { value: 'totalTasksOpenedByUser', display: 'Tasks Opened' }
  ], []);

  const userActionOptions = useMemo(() =>
    selectedActions.map(action => ({
      value: action,
      display: UserActionLabels[action]
    })),
    [selectedActions]
  );

  const processingTimeOptions = useMemo(() => [
    { value: 'timeToOpen', display: 'Time to Open' },
    { value: 'timeToReview', display: 'Turnaround Time' },
    { value: 'timeToFileAway', display: 'Time to Review' }
  ], []);

  const processingTimeSubtitles = useMemo(() => ({
    'timeToOpen': 'Time taken by users to first open a document',
    'timeToReview': 'Time from first opening a document to filing it away',
    'timeToFileAway': 'Time taken by users to review a document'
  }), []);

  const processingTimeXAxisLabels = useMemo(() => ({
    'timeToOpen': 'Time document was first received by MedPrompt',
    'timeToReview': 'Time document first opened by user',
    'timeToFileAway': 'Time review began by user'
  }), []);

  const consultationProcessingTimeOptions = useMemo(() => [
    { value: 'recordingDuration', display: 'Recording Duration' },
    { value: 'timeToReview', display: 'Time to File' }
  ], []);

  const consultationProcessingTimeSubtitles = useMemo(() => ({
    recordingDuration: 'Length of each consultation recording in the selected time window',
    timeToReview: 'Time from notes ready for review to filing the consultation away'
  }), []);

  const consultationProcessingTimeXAxisLabels = useMemo(() => ({
    recordingDuration: 'Time recording was completed',
    timeToReview: 'Time recording was completed'
  }), []);

  const dateRangeOptions = useMemo(() => [
    { value: 'last24h', display: 'Last 24h' },
    { value: 'last7days', display: 'Last 7 days' },
    { value: 'lastMonth', display: 'Last month' },
    { value: 'custom', display: 'Custom' }
  ], []);

  const insightsTypeOptions = useMemo(() => ALL_INSIGHTS_TYPE_OPTIONS.filter((option) => {
    if (option.value === 'documents') return hasDocsRead;
    if (option.value === 'tasks') return hasTaskRead;
    if (option.value === 'consultations') return hasConsultRead;
    return false;
  }), [hasDocsRead, hasTaskRead, hasConsultRead]);

  useEffect(() => {
    if (insightsTypeOptions.length === 0) return;
    const selected = insightsTypeOptions.find((option) => option.value === insightsType);
    if (selected) {
      insightsTypeDropdown.setSelected(selected);
      return;
    }
    const fallback = insightsTypeOptions[0];
    insightsTypeDropdown.setSelected(fallback);
    setInsightsType(fallback.value);
    const defaults = defaultFiltersForInsightsType(fallback.value);
    setSelectedActions(defaults.selectedActions);
    setFocusedAction(defaults.focusedAction);
  }, [insightsType, insightsTypeOptions]);

  const selectedInsightsTypeIcon = useMemo(() => {
    const selected = insightsTypeOptions.find((option) => option.value === insightsType);
    return selected?.icon || faFileAlt;
  }, [insightsType, insightsTypeOptions]);

  return {
    insightsType,
    viewMode,
    selectedUserIds,
    selectedActions,
    focusedAction,
    dateRange,
    customDateRange,
    dateRangeParams,
    dateRangeDropdown,
    insightsTypeDropdown,
    documentsReadyDropdown,
    tasksReadyDropdown,
    processingTimeDropdown,
    consultationProcessingTimeDropdown,
    focusedActionDropdown,
    filterBarActionOpen,
    setFilterBarActionOpen,
    overTimeDropdownOpen,
    setOverTimeDropdownOpen,
    byStaffDropdownOpen,
    setByStaffDropdownOpen,
    userFilterOpen,
    setUserFilterOpen,
    actionFilterOpen,
    setActionFilterOpen,
    getExpandedUserIds,
    handleUserSelectionChange,
    handleActionSelectionChange,
    handleFocusedActionChange,
    handleToggleAction,
    getDateRange,
    handleDateRangeSelect,
    handleCustomDateChange,
    handleViewModeChange,
    handleInsightsTypeChange,
    getActionTitle,
    getUserSelectionText,
    getUsersAndTeamsOptions,
    getActionOptions,
    documentsReadyOptions,
    tasksReadyOptions,
    userActionOptions,
    processingTimeOptions,
    processingTimeSubtitles,
    processingTimeXAxisLabels,
    consultationProcessingTimeOptions,
    consultationProcessingTimeSubtitles,
    consultationProcessingTimeXAxisLabels,
    dateRangeOptions,
    insightsTypeOptions,
    selectedInsightsTypeIcon,
  };
};

export default useInsightsQuery;
